import { z } from "zod";
import {
  CreateResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { getAuthenticatedServerClient, schemas } from "../api/server.api";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { MoneySchema } from "../model/Money";

type SubscriptionPackageDto = z.infer<typeof schemas.SubscriptionPackageDto>;

const SubscriptionPackageSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  price: MoneySchema,
  features: z.array(z.string()),
  recommended: z.boolean(),
  drivingSchoolId: z.string(),
});
export type SubscriptionPackage = z.output<typeof SubscriptionPackageSchema>;

class SubscriptionPackagesProvider extends AbstractDataProvider<SubscriptionPackage> {
  async getOne(_: string, { id }: GetOneParams<SubscriptionPackage>): Promise<GetOneResult<SubscriptionPackage>> {
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.getSubscriptionPackage({ params: { id } });
    return { data: _dtoToRecord(dto) };
  }

  async getList(_resource: string, params: GetListParams): Promise<GetListResult<SubscriptionPackage>> {
    const { filter, sort, pagination } = params;
    const { drivingSchoolId, ...clientSideFilter } = filter;
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    const serverClient = await getAuthenticatedServerClient();
    const dtos = await serverClient.listSubscriptionPackages({ queries: { drivingSchoolId } });
    const records = dtos.map(_dtoToRecord);
    return applyPagination(applySort(applyFilter(records, clientSideFilter), sort), pagination);
  }

  async create(
    _resource: string,
    params: { data: Pick<SubscriptionPackage, "drivingSchoolId" | "name" | "price" | "features" | "recommended"> },
  ): Promise<CreateResult<SubscriptionPackage>> {
    const { data } = params;
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.createSubscriptionPackage({
      name: data.name,
      price: data.price,
      features: data.features,
      recommended: data.recommended,
      drivingSchoolId: data.drivingSchoolId,
    });
    return { data: _dtoToRecord(dto) };
  }

  async update(
    _resource: string,
    params: UpdateParams<Omit<SubscriptionPackage, "drivingSchoolId">>,
  ): Promise<UpdateResult<SubscriptionPackage>> {
    const { id, data } = params;
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.updateSubscriptionPackage(
      {
        name: data.name,
        price: data.price,
        features: data.features,
        recommended: data.recommended,
      },
      { params: { id } },
    );
    return { data: _dtoToRecord(dto) };
  }

  async delete(_resource: string, params: DeleteParams): Promise<DeleteResult> {
    const { id } = params;
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteSubscriptionPackage(undefined, { params: { id } });
    return { data: {} };
  }
}

export const subscriptionPackagesProvider = new SubscriptionPackagesProvider();

function _dtoToRecord(dto: SubscriptionPackageDto): SubscriptionPackage {
  return SubscriptionPackageSchema.parse(dto);
}
